import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Password } from 'primereact/password';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { Divider } from 'primereact/divider';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { API_URLS } from '../../Services/servicioURL';
import * as XLSX from 'xlsx';

// Interfaces - CORREGIDO: campo sucursal siempre presente
interface EmpleadoType {
    cveemp: string;
    nomemp: string;
    password: string;
    sucursal: string; // CORREGIDO: siempre string, puede estar vacío
}

interface FormState {
    cveemp: string;
    nomemp: string;
    password: string;
    sucursal: string; // CORREGIDO: siempre string
}

interface FormErrors {
    cveemp?: string;
    nomemp?: string;
    password?: string;
    sucursal?: string;
}

interface FilterState {
    searchTerm: string;
}

interface ExcelEmpleado {
    cveemp: string;
    nomemp: string;
    password?: string;
    sucursal: string; // CORREGIDO: siempre string
    fila?: number;
}

interface CargaMasivaResult {
    exitosos: number;
    errores: number;
    duplicados: number;
    detalles: {
        fila: number;
        cveemp: string;
        nomemp: string;
        sucursal: string;
        passwordGenerada?: string;
        error?: string;
        estado: 'exitoso' | 'error' | 'duplicado';
    }[];
}

type Mode = 'view' | 'edit' | 'create';

// Utility functions
const fetchWithErrorHandling = async (url: string, options?: RequestInit) => {
    try {
        const response = await fetch(url, options);
        if (!response.ok) {
            const errorText = await response.text();
            console.error(`❌ HTTP Error ${response.status}:`, errorText);
            throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
        }
        return await response.json();
    } catch (error) {
        console.error(`❌ Error fetching ${url}:`, error);
        throw error;
    }
};

const obtenerEmpleadosAPI = (): Promise<EmpleadoType[]> => 
    fetchWithErrorHandling(API_URLS.empleados.obtener);

// Función para generar contraseña automáticamente
const generarPasswordAutomatica = (cveemp: string, nomemp: string): string => {
    try {
        const palabras = nomemp
            .trim()
            .split(/\s+/)
            .filter(palabra => palabra.length > 0);
        
        const iniciales = palabras
            .map(palabra => palabra.charAt(0).toUpperCase())
            .join('');
        
        const codigoStr = cveemp.toString();
        let ultimosDigitos = '';
        
        if (codigoStr.length >= 2) {
            ultimosDigitos = codigoStr.slice(-2);
        } else {
            ultimosDigitos = codigoStr;
        }
        
        const passwordGenerada = iniciales + ultimosDigitos;
        
        console.log(`🔐 Generando password para ${cveemp} - ${nomemp}:`);
        console.log(`   Palabras: [${palabras.join(', ')}]`);
        console.log(`   Iniciales: ${iniciales}`);
        console.log(`   Últimos dígitos: ${ultimosDigitos}`);
        console.log(`   Password generada: ${passwordGenerada}`);
        
        return passwordGenerada;
    } catch (error) {
        console.error('Error generando password:', error);
        const fallback = nomemp.substring(0, 2).toUpperCase() + cveemp;
        console.log(`🔄 Usando password fallback: ${fallback}`);
        return fallback;
    }
};

const validarEstructuraExcel = (data: any[]): { valido: boolean; mensaje: string } => {
    if (!data || data.length === 0) {
        return { valido: false, mensaje: 'El archivo está vacío' };
    }

    const primeraFila = data[0];
    const columnasRequeridas = ['cveemp', 'nomemp'];
    const columnasEncontradas = Object.keys(primeraFila).map(k => k.toLowerCase());

    const columnasFaltantes = columnasRequeridas.filter(col => 
        !columnasEncontradas.includes(col)
    );

    if (columnasFaltantes.length > 0) {
        return { 
            valido: false, 
            mensaje: `Faltan las siguientes columnas obligatorias: ${columnasFaltantes.join(', ')}. Las columnas deben ser: cveemp, nomemp. Opcional: sucursal` 
        };
    }

    const filasValidas = data.filter(row => {
        const cveemp = String(row.cveemp || row.CVEEMP || '').trim();
        const nomemp = String(row.nomemp || row.NOMEMP || '').trim();
        return cveemp && nomemp;
    });

    if (filasValidas.length === 0) {
        return {
            valido: false,
            mensaje: 'No se encontraron filas con datos válidos (cveemp y nomemp requeridos)'
        };
    }

    return { valido: true, mensaje: 'Estructura válida' };
};

// CORREGIDO: Función para procesar archivo Excel
const procesarArchivoExcel = (file: File): Promise<ExcelEmpleado[]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                const validacion = validarEstructuraExcel(jsonData);
                if (!validacion.valido) {
                    reject(new Error(validacion.mensaje));
                    return;
                }

                const empleados: ExcelEmpleado[] = jsonData.map((row: any, index) => {
                    const cveemp = String(row.cveemp || row.CVEEMP || '').trim();
                    const nomemp = String(row.nomemp || row.NOMEMP || '').trim();
                    
                    // CORREGIDO: Manejo consistente del campo sucursal
                    let sucursal = '';
                    const sucursalRaw = row.sucursal || row.SUCURSAL || '';
                    if (sucursalRaw !== null && sucursalRaw !== undefined) {
                        sucursal = String(sucursalRaw).trim();
                    }
                    
                    const password = generarPasswordAutomatica(cveemp, nomemp);
                    
                    console.log(`📋 Procesando fila ${index + 2}:`, {
                        cveemp,
                        nomemp,
                        sucursal: sucursal || '(vacío)',
                        password
                    });
                    
                    return {
                        cveemp,
                        nomemp,
                        sucursal,
                        password,
                        fila: index + 2
                    };
                });

                resolve(empleados);
            } catch (error) {
                reject(new Error('Error al procesar el archivo Excel: ' + error));
            }
        };

        reader.onerror = () => {
            reject(new Error('Error al leer el archivo'));
        };

        reader.readAsArrayBuffer(file);
    });
};

// CORREGIDO: Custom hooks
const useFormState = () => {
    const [formState, setFormState] = useState<FormState>({
        cveemp: '',
        nomemp: '',
        password: '',
        sucursal: '' // CORREGIDO: siempre string vacío por defecto
    });

    const updateFormState = useCallback((updates: Partial<FormState>) => {
        setFormState(prev => ({ ...prev, ...updates }));
    }, []);

    const resetFormState = useCallback(() => {
        setFormState({
            cveemp: '',
            nomemp: '',
            password: '',
            sucursal: ''
        });
    }, []);

    return { formState, updateFormState, resetFormState };
};

const useFilterState = () => {
    const [filterState, setFilterState] = useState<FilterState>({
        searchTerm: ''
    });

    const updateFilter = useCallback((updates: Partial<FilterState>) => {
        setFilterState(prev => ({ ...prev, ...updates }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilterState({
            searchTerm: ''
        });
    }, []);

    return { filterState, updateFilter, resetFilters };
};

// Validaciones
const validarFormulario = (formState: FormState, mode: Mode): FormErrors => {
    const errors: FormErrors = {};
    
    if (!formState.cveemp.trim()) {
        errors.cveemp = 'El código de empleado es obligatorio';
    } else if (formState.cveemp.length > 10) {
        errors.cveemp = 'El código no puede tener más de 10 caracteres';
    }
    
    if (!formState.nomemp.trim()) {
        errors.nomemp = 'El nombre del empleado es obligatorio';
    } else if (formState.nomemp.length > 200) {
        errors.nomemp = 'El nombre no puede tener más de 200 caracteres';
    }
    
    if (!formState.password.trim()) {
        errors.password = 'La contraseña es obligatoria';
    } else if (formState.password.length < 6) {
        errors.password = 'La contraseña debe tener al menos 6 caracteres';
    } else if (formState.password.length > 100) {
        errors.password = 'La contraseña no puede tener más de 100 caracteres';
    }
    
    // Sucursal es opcional, solo validar si se proporciona
    if (formState.sucursal && formState.sucursal.length > 15) {
        errors.sucursal = 'La sucursal no puede tener más de 15 caracteres';
    }
    
    return errors;
};

const validarEmpleadoExcel = (empleado: ExcelEmpleado): string | null => {
    if (!empleado.cveemp) return 'Código de empleado vacío';
    if (!empleado.nomemp) return 'Nombre de empleado vacío';
    if (empleado.cveemp.length > 10) return 'Código muy largo (máx. 10 caracteres)';
    if (empleado.nomemp.length > 200) return 'Nombre muy largo (máx. 200 caracteres)';
    
    if (empleado.sucursal && empleado.sucursal.length > 15) {
        return 'Sucursal muy larga (máx. 15 caracteres)';
    }
    
    const palabras = empleado.nomemp.trim().split(/\s+/).filter(p => p.length > 0);
    if (palabras.length === 0) return 'El nombre debe contener al menos una palabra';
    
    return null;
};

// Hook para detectar tamaño de pantalla
const useScreenSize = () => {
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
        isMobile: window.innerWidth < 768,
        isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
        isDesktop: window.innerWidth >= 1024
    });

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setScreenSize({
                width,
                height: window.innerHeight,
                isMobile: width < 768,
                isTablet: width >= 768 && width < 1024,
                isDesktop: width >= 1024
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return screenSize;
};

// CORREGIDO: Función para crear payload consistente
const crearPayloadEmpleado = (empleado: { cveemp: string; nomemp: string; password: string; sucursal: string }) => {
    const payload = {
        cveemp: empleado.cveemp.trim(),
        nomemp: empleado.nomemp.trim(),
        password: empleado.password.trim(),
        sucursal: empleado.sucursal.trim() // CORREGIDO: siempre incluir, aunque esté vacío
    };
    
    console.log('📤 Payload creado:', payload);
    return payload;
};

// Main component
const EmpleadosAdmin: React.FC = () => {
    // State
    const [empleados, setEmpleados] = useState<EmpleadoType[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [mode, setMode] = useState<Mode>('view');
    const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState<EmpleadoType | null>(null);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [errors, setErrors] = useState<FormErrors>({});
    const [submitting, setSubmitting] = useState<boolean>(false);
    
    // Estados para carga masiva
    const [showCargaMasivaDialog, setShowCargaMasivaDialog] = useState<boolean>(false);
    const [procesandoCargaMasiva, setProcesandoCargaMasiva] = useState<boolean>(false);
    const [progresoCarga, setProgresoCarga] = useState<number>(0);
    const [resultadoCarga, setResultadoCarga] = useState<CargaMasivaResult | null>(null);
    const [showResultadoDialog, setShowResultadoDialog] = useState<boolean>(false);
    
    const { formState, updateFormState, resetFormState } = useFormState();
    const { filterState, updateFilter, resetFilters } = useFilterState();
    const screenSize = useScreenSize();
    
    const toast = useRef<Toast>(null);
    const fileUploadRef = useRef<FileUpload>(null);

    // CORREGIDO: Memoized values - incluye sucursal en búsqueda
    const filteredEmpleados = useMemo(() => {
        if (!Array.isArray(empleados)) return [];
        
        let filtered = [...empleados];

        if (filterState.searchTerm) {
            const searchLower = filterState.searchTerm.toLowerCase();
            filtered = filtered.filter(empleado => {
                const cveemp = empleado.cveemp?.toLowerCase() || '';
                const nomemp = empleado.nomemp?.toLowerCase() || '';
                const sucursal = empleado.sucursal?.toLowerCase() || '';
                
                return cveemp.includes(searchLower) ||
                       nomemp.includes(searchLower) ||
                       sucursal.includes(searchLower);
            });
        }

        return filtered;
    }, [empleados, filterState]);

    // API functions
    const cargarEmpleados = useCallback(async () => {
        setLoading(true);
        try {
            const empleadosData = await obtenerEmpleadosAPI();
            console.log('📥 Empleados cargados:', empleadosData);
            
            // CORREGIDO: Asegurar que todos los empleados tengan el campo sucursal
            const empleadosNormalizados = empleadosData.map(emp => ({
                ...emp,
                sucursal: emp.sucursal || '' // Asegurar que sucursal siempre sea string
            }));
            
            setEmpleados(Array.isArray(empleadosNormalizados) ? empleadosNormalizados : []);
        } catch (error) {
            console.error('❌ Error loading empleados:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al cargar los empleados',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    }, []);

    // CORREGIDO: Función para procesar carga masiva
    const procesarCargaMasiva = useCallback(async (empleadosExcel: ExcelEmpleado[]) => {
        setProcesandoCargaMasiva(true);
        setProgresoCarga(0);

        const resultado: CargaMasivaResult = {
            exitosos: 0,
            errores: 0,
            duplicados: 0,
            detalles: []
        };

        // CORREGIDO: Crear mapa de empleados existentes más robusto
        const empleadosExistentes = new Map();
        empleados.forEach(emp => {
            empleadosExistentes.set(emp.cveemp.toLowerCase(), emp);
        });

        console.log('📊 Iniciando carga masiva:', {
            totalEmpleados: empleadosExcel.length,
            empleadosExistentes: empleadosExistentes.size
        });

        for (let i = 0; i < empleadosExcel.length; i++) {
            const empleado = empleadosExcel[i];
            const progreso = ((i + 1) / empleadosExcel.length) * 100;
            setProgresoCarga(progreso);

            console.log(`🔄 Procesando empleado ${i + 1}/${empleadosExcel.length}:`, empleado);

            const errorValidacion = validarEmpleadoExcel(empleado);
            if (errorValidacion) {
                console.log(`❌ Error de validación:`, errorValidacion);
                resultado.errores++;
                resultado.detalles.push({
                    fila: empleado.fila || i + 2,
                    cveemp: empleado.cveemp,
                    nomemp: empleado.nomemp,
                    sucursal: empleado.sucursal,
                    error: errorValidacion,
                    estado: 'error'
                });
                continue;
            }

            // CORREGIDO: Verificación de duplicados más específica
            const empleadoExistente = empleadosExistentes.get(empleado.cveemp.toLowerCase());
            if (empleadoExistente) {
                console.log(`⚠️ Empleado duplicado encontrado:`, empleadoExistente);
                resultado.duplicados++;
                resultado.detalles.push({
                    fila: empleado.fila || i + 2,
                    cveemp: empleado.cveemp,
                    nomemp: empleado.nomemp,
                    sucursal: empleado.sucursal,
                    passwordGenerada: empleado.password,
                    error: `Empleado ya existe (Sucursal actual: ${empleadoExistente.sucursal || 'Sin sucursal'})`,
                    estado: 'duplicado'
                });
                continue;
            }

            try {
                // CORREGIDO: Usar función consistente para crear payload
                const payload = crearPayloadEmpleado({
                    cveemp: empleado.cveemp,
                    nomemp: empleado.nomemp,
                    password: empleado.password || '',
                    sucursal: empleado.sucursal
                });

                console.log(`📤 Enviando empleado ${empleado.cveemp}:`, payload);

                const response = await fetchWithErrorHandling(API_URLS.empleados.registrar, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                console.log(`✅ Empleado creado exitosamente:`, response);

                resultado.exitosos++;
                resultado.detalles.push({
                    fila: empleado.fila || i + 2,
                    cveemp: empleado.cveemp,
                    nomemp: empleado.nomemp,
                    sucursal: empleado.sucursal,
                    passwordGenerada: empleado.password,
                    estado: 'exitoso'
                });

                // Agregar al mapa de existentes
                empleadosExistentes.set(empleado.cveemp.toLowerCase(), {
                    cveemp: empleado.cveemp,
                    nomemp: empleado.nomemp,
                    sucursal: empleado.sucursal,
                    password: empleado.password
                });

            } catch (error: any) {
                console.error(`❌ Error al crear empleado ${empleado.cveemp}:`, error);
                
                resultado.errores++;
                let mensajeError = 'Error al crear empleado';
                
                if (error?.message) {
                    if (error.message.includes('duplicate') || error.message.includes('unique') || error.message.includes('Duplicate')) {
                        mensajeError = 'Código de empleado duplicado';
                    } else if (error.message.includes('400')) {
                        mensajeError = 'Datos inválidos - verificar formato';
                    } else if (error.message.includes('500')) {
                        mensajeError = 'Error interno del servidor';
                    } else {
                        mensajeError = error.message;
                    }
                }

                resultado.detalles.push({
                    fila: empleado.fila || i + 2,
                    cveemp: empleado.cveemp,
                    nomemp: empleado.nomemp,
                    sucursal: empleado.sucursal,
                    passwordGenerada: empleado.password,
                    error: mensajeError,
                    estado: 'error'
                });
            }

            // Pausa pequeña cada 10 empleados para no sobrecargar
            if (i % 10 === 0) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        console.log('📊 Resultado final de carga masiva:', resultado);

        setResultadoCarga(resultado);
        setProcesandoCargaMasiva(false);
        setShowCargaMasivaDialog(false);
        setShowResultadoDialog(true);

        await cargarEmpleados();

        toast.current?.show({
            severity: resultado.errores > 0 ? 'warn' : 'success',
            summary: 'Carga masiva completada',
            detail: `Exitosos: ${resultado.exitosos}, Errores: ${resultado.errores}, Duplicados: ${resultado.duplicados}`,
            life: 5000
        });
    }, [empleados, cargarEmpleados]);

    // Event handlers
    const handleNuevo = useCallback(() => {
        resetFormState();
        setErrors({});
        setMode('create');
        setEmpleadoSeleccionado(null);
        setShowDialog(true);
    }, [resetFormState]);

    const handleCargaMasiva = useCallback(() => {
        setShowCargaMasivaDialog(true);
    }, []);

    const handleArchivoSeleccionado = useCallback(async (event: any) => {
        const file = event.files[0];
        if (!file) return;

        try {
            const empleadosExcel = await procesarArchivoExcel(file);
            
            if (empleadosExcel.length === 0) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Archivo vacío',
                    detail: 'No se encontraron empleados en el archivo',
                    life: 3000
                });
                return;
            }

            const preview = empleadosExcel.slice(0, 3).map(emp => 
                `|| ${emp.cveemp} - ${emp.nomemp}${emp.sucursal ? ' - Sucursal: ' + emp.sucursal : ' - Sin sucursal'} → ${emp.password} ||`
            ).join('\n');

            confirmDialog({
                message: `Se encontraron ${empleadosExcel.length} empleados en el archivo.\n\nEjemplos de contraseñas que se generarán:\n ${preview} \n\n¿Desea proceder con la carga masiva?`,
                header: 'Confirmar carga masiva',
                icon: 'pi pi-question-circle',
                accept: () => {
                    procesarCargaMasiva(empleadosExcel);
                },
                reject: () => {
                    fileUploadRef.current?.clear();
                }
            });

        } catch (error: any) {
            console.error('❌ Error processing Excel file:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Error al procesar el archivo Excel',
                life: 5000
            });
            fileUploadRef.current?.clear();
        }
    }, [procesarCargaMasiva]);

    // CORREGIDO: incluye sucursal en editar
    const handleEditar = useCallback((empleado: EmpleadoType) => {
        updateFormState({
            cveemp: empleado.cveemp || '',
            nomemp: empleado.nomemp || '',
            password: empleado.password || '',
            sucursal: empleado.sucursal || '' // CORREGIDO: siempre string
        });
        setErrors({});
        setMode('edit');
        setEmpleadoSeleccionado(empleado);
        setShowDialog(true);
    }, [updateFormState]);

    const handleEliminar = useCallback((empleado: EmpleadoType) => {
        confirmDialog({
            message: `¿Está seguro de eliminar al empleado: ${empleado.nomemp} (${empleado.cveemp})?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await fetchWithErrorHandling(API_URLS.empleados.eliminar(empleado.cveemp), {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json' }
                    });

                    toast.current?.show({
                        severity: 'success',
                        summary: 'Éxito',
                        detail: 'Empleado eliminado correctamente',
                        life: 2000
                    });

                    await cargarEmpleados();
                } catch (error) {
                    console.error('❌ Error deleting empleado:', error);
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudo eliminar el empleado',
                        life: 3000
                    });
                }
            }
        });
    }, [cargarEmpleados]);

    // CORREGIDO: incluye sucursal en guardar
    const handleGuardar = useCallback(async () => {
        const formErrors = validarFormulario(formState, mode);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            toast.current?.show({
                severity: 'warn',
                summary: 'Datos incompletos',
                detail: 'Por favor corrija los errores en el formulario',
                life: 3000
            });
            return;
        }

        setSubmitting(true);
        try {
            // CORREGIDO: Usar función consistente para crear payload
            const payload = crearPayloadEmpleado(formState);

            console.log(`📤 Guardando empleado (${mode}):`, payload);

            if (mode === 'create') {
                const response = await fetchWithErrorHandling(API_URLS.empleados.registrar, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                console.log('✅ Empleado creado:', response);

                toast.current?.show({
                    severity: 'success',
                    summary: 'Éxito',
                    detail: 'Empleado creado exitosamente',
                    life: 2000
                });
            } else if (mode === 'edit' && empleadoSeleccionado) {
                const response = await fetchWithErrorHandling(API_URLS.empleados.actualizar(empleadoSeleccionado.cveemp), {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                console.log('✅ Empleado actualizado:', response);

                toast.current?.show({
                    severity: 'success',
                    summary: 'Éxito',
                    detail: 'Empleado actualizado exitosamente',
                    life: 2000
                });
            }

            setShowDialog(false);
            await cargarEmpleados();
        } catch (error: any) {
            console.error('❌ Error saving empleado:', error);
            
            let errorMessage = 'Error al guardar el empleado';
            if (error?.message) {
                if (error.message.includes('duplicate') || error.message.includes('unique') || error.message.includes('Duplicate')) {
                    errorMessage = 'Ya existe un empleado con ese código';
                    setErrors({ cveemp: 'Código ya existe' });
                } else if (error.message.includes('400')) {
                    errorMessage = 'Datos inválidos - verificar formato';
                } else if (error.message.includes('500')) {
                    errorMessage = 'Error interno del servidor';
                } else {
                    errorMessage = error.message;
                }
            }
            
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 3000
            });
        } finally {
            setSubmitting(false);
        }
    }, [formState, mode, empleadoSeleccionado, cargarEmpleados]);

    const handleCancelar = useCallback(() => {
        setShowDialog(false);
        resetFormState();
        setErrors({});
        setEmpleadoSeleccionado(null);
    }, [resetFormState]);

    const handleCancelarCargaMasiva = useCallback(() => {
        setShowCargaMasivaDialog(false);
        fileUploadRef.current?.clear();
    }, []);

    // CORREGIDO: plantilla Excel incluye sucursal
    const descargarPlantillaExcel = useCallback(() => {
        const plantilla = [
            { cveemp: '6744', nomemp: 'Ivan Armando Inés Galicia', sucursal: '001' },
            { cveemp: '9', nomemp: 'Juan Pérez López', sucursal: '002' },
            { cveemp: '1234', nomemp: 'María Elena Rodríguez Sánchez', sucursal: '' } // Ejemplo con sucursal vacía
        ];

        const ws = XLSX.utils.json_to_sheet(plantilla);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Empleados');
        XLSX.writeFile(wb, 'plantilla_empleados.xlsx');

        toast.current?.show({
            severity: 'info',
            summary: 'Plantilla descargada',
            detail: 'Se ha descargado la plantilla. El campo sucursal es opcional.',
            life: 3000
        });
    }, []);

    // Column templates
    const accionesBodyTemplate = useCallback((rowData: EmpleadoType) => (
        <div style={{ 
            display: 'flex', 
            gap: screenSize.isMobile ? '4px' : '8px', 
            justifyContent: 'center',
            flexWrap: 'wrap'
        }}>
            <Button
                icon="pi pi-pencil"
                size="small"
                severity="info"
                outlined
                rounded
                onClick={() => handleEditar(rowData)}
                tooltip="Editar empleado"
                tooltipOptions={{ position: 'top' }}
                style={{ 
                    width: screenSize.isMobile ? '28px' : '32px', 
                    height: screenSize.isMobile ? '28px' : '32px',
                    fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem'
                }}
            />
            <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                outlined
                rounded
                onClick={() => handleEliminar(rowData)}
                tooltip="Eliminar empleado"
                tooltipOptions={{ position: 'top' }}
                style={{ 
                    width: screenSize.isMobile ? '28px' : '32px', 
                    height: screenSize.isMobile ? '28px' : '32px',
                    fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem'
                }}
            />
        </div>
    ), [handleEditar, handleEliminar, screenSize]);

    const passwordBodyTemplate = useCallback((rowData: EmpleadoType) => (
        <Badge 
            value={rowData.password || 'N/A'} 
            severity="secondary"
            style={{ 
                fontFamily: 'monospace',
                fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem',
                padding: screenSize.isMobile ? '2px 6px' : '4px 8px',
                wordBreak: 'break-all'
            }}
        />
    ), [screenSize]);

    const codigoBodyTemplate = useCallback((rowData: EmpleadoType) => (
        <Badge 
            value={rowData.cveemp} 
            severity="info"
            style={{ 
                fontWeight: '600',
                fontSize: screenSize.isMobile ? '0.75rem' : '0.85rem',
                wordBreak: 'break-all'
            }}
        />
    ), [screenSize]);

    // CORREGIDO: Template para Sucursal responsivo
    const sucursalBodyTemplate = useCallback((rowData: EmpleadoType) => (
        <div style={{ textAlign: 'center' }}>
            {rowData.sucursal && rowData.sucursal.trim() !== '' ? (
                <Badge 
                    value={rowData.sucursal} 
                    severity="success"
                    style={{ 
                        fontFamily: 'monospace',
                        fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem',
                        padding: screenSize.isMobile ? '2px 6px' : '4px 8px',
                        wordBreak: 'break-all'
                    }}
                />
            ) : (
                <span style={{ 
                    color: '#9ca3af', 
                    fontStyle: 'italic', 
                    fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem'
                }}>
                    Sin Sucursal
                </span>
            )}
        </div>
    ), [screenSize]);

    // Template para nombre responsivo
    const nombreBodyTemplate = useCallback((rowData: EmpleadoType) => (
        <div style={{ 
            color: '#374151',
            fontWeight: '500',
            fontSize: screenSize.isMobile ? '0.8rem' : '0.9rem',
            lineHeight: '1.2',
            wordBreak: 'break-word',
            hyphens: 'auto'
        }}>
            {rowData.nomemp}
        </div>
    ), [screenSize]);

    // Effects
    useEffect(() => {
        cargarEmpleados();
    }, [cargarEmpleados]);

    const dialogHeader = mode === 'create' ? '👤 Nuevo Empleado' : '✏️ Editar Empleado';

    // Configuración responsiva de la tabla
    const getTableConfig = () => {
        if (screenSize.isMobile) {
            return {
                responsiveLayout: 'stack' as const,
                breakpoint: '768px',
                rows: 10,
                rowsPerPageOptions: [5, 10, 15],
                scrollable: false,
                style: { fontSize: '0.8rem' }
            };
        } else if (screenSize.isTablet) {
            return {
                responsiveLayout: 'scroll' as const,
                breakpoint: '1024px',
                rows: 12,
                rowsPerPageOptions: [10, 12, 20],
                scrollable: true,
                style: { fontSize: '0.85rem' }
            };
        } else {
            return {
                responsiveLayout: 'scroll' as const,
                breakpoint: '1200px',
                rows: 15,
                rowsPerPageOptions: [10, 15, 25, 50],
                scrollable: true,
                style: { fontSize: '0.9rem' }
            };
        }
    };

    const tableConfig = getTableConfig();

    return (
        <>
            <Navbar />
            <Sidebar />
            <Toast ref={toast} />
            <ConfirmDialog />
            
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: screenSize.isMobile ? '10px' : '20px'
            }}>
                
                {/* Header principal */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: screenSize.isMobile ? '16px' : '24px',
                    marginBottom: '20px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: screenSize.isMobile ? 'flex-start' : 'center', 
                        marginBottom: '16px',
                        flexDirection: screenSize.isMobile ? 'column' : 'row',
                        gap: screenSize.isMobile ? '12px' : '0'
                    }}>
                        <h1 style={{ 
                            margin: 0, 
                            color: '#1f2937', 
                            fontSize: screenSize.isMobile ? '1.5rem' : '2rem', 
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            lineHeight: '1.2'
                        }}>
                            👥 Administración de Empleados
                        </h1>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: screenSize.isMobile ? '6px 12px' : '8px 16px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: screenSize.isMobile ? '0.75rem' : '0.85rem',
                            fontWeight: '600'
                        }}>
                            <i className="pi pi-users"></i>
                            {loading ? 'CARGANDO...' : `${filteredEmpleados.length} EMPLEADOS`}
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                </div>

                {/* Controles de búsqueda y acciones */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: screenSize.isMobile ? '16px' : '20px',
                    marginBottom: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        gap: screenSize.isMobile ? '12px' : '16px', 
                        alignItems: screenSize.isMobile ? 'stretch' : 'center',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        flexDirection: screenSize.isMobile ? 'column' : 'row'
                    }}>
                        {/* Barra de búsqueda - CORREGIDO: incluye Sucursal */}
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '12px', 
                            flex: screenSize.isMobile ? 'none' : 1, 
                            minWidth: screenSize.isMobile ? 'auto' : '300px',
                            width: screenSize.isMobile ? '100%' : 'auto'
                        }}>
                            <div style={{ 
                                position: 'relative', 
                                flex: 1, 
                                maxWidth: screenSize.isMobile ? 'none' : '400px',
                                width: '100%'
                            }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    value={filterState.searchTerm}
                                    onChange={(e) => updateFilter({ searchTerm: e.target.value })}
                                    placeholder={screenSize.isMobile ? "🔍 Buscar..." : "🔍 Buscar por código, nombre o sucursal..."}
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        paddingLeft: '40px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: screenSize.isMobile ? '0.9rem' : '0.95rem'
                                    }}
                                />
                            </div>
                            {!screenSize.isMobile && (
                                <Button 
                                    icon="pi pi-filter-slash" 
                                    label="Limpiar" 
                                    severity="secondary"
                                    outlined
                                    onClick={resetFilters}
                                    style={{ 
                                        height: '40px',
                                        borderRadius: '8px'
                                    }}
                                />
                            )}
                        </div>

                        {/* Botones de acción */}
                        <div style={{ 
                            display: 'flex', 
                            gap: screenSize.isMobile ? '8px' : '12px',
                            width: screenSize.isMobile ? '100%' : 'auto',
                            justifyContent: screenSize.isMobile ? 'space-between' : 'flex-end'
                        }}>
                            {screenSize.isMobile && (
                                <Button 
                                    icon="pi pi-filter-slash" 
                                    severity="secondary"
                                    outlined
                                    onClick={resetFilters}
                                    style={{ 
                                        height: '40px',
                                        borderRadius: '8px',
                                        flex: '0 0 auto'
                                    }}
                                    tooltip="Limpiar filtros"
                                />
                            )}
                            <Button 
                                icon="pi pi-upload" 
                                label={screenSize.isMobile ? "Carga" : "Carga Masiva"}
                                severity="info"
                                onClick={handleCargaMasiva}
                                style={{ 
                                    height: '40px',
                                    fontSize: screenSize.isMobile ? '0.8rem' : '0.9rem',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    minWidth: screenSize.isMobile ? 'auto' : '140px',
                                    flex: screenSize.isMobile ? '1' : '0 0 auto'
                                }}
                                tooltip="Cargar empleados desde Excel"
                            />
                            <Button 
                                icon="pi pi-plus" 
                                label={screenSize.isMobile ? "Nuevo" : "Nuevo Empleado"}
                                severity="success"
                                onClick={handleNuevo}
                                style={{ 
                                    height: '40px',
                                    fontSize: screenSize.isMobile ? '0.8rem' : '0.9rem',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    minWidth: screenSize.isMobile ? 'auto' : '160px',
                                    flex: screenSize.isMobile ? '1' : '0 0 auto'
                                }}
                                tooltip="Crear nuevo empleado"
                            />
                        </div>
                    </div>
                </div>

                {/* Tabla de empleados - COMPLETAMENTE RESPONSIVA */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: screenSize.isMobile ? '12px' : '24px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    overflow: 'hidden'
                }}>
                    <DataTable 
                        value={filteredEmpleados} 
                        responsiveLayout={tableConfig.responsiveLayout}
                        breakpoint={tableConfig.breakpoint}
                        stripedRows
                        size="small"
                        loading={loading}
                        emptyMessage={loading ? '🔄 Cargando empleados...' : '👤 No hay empleados registrados que coincidan con la búsqueda'}
                        className="p-datatable-sm"
                        style={{ 
                            ...tableConfig.style,
                            borderRadius: '8px',
                            overflow: 'hidden',
                            width: '100%'
                        }}
                        scrollable={tableConfig.scrollable}
                        scrollHeight={screenSize.isMobile ? "400px" : "600px"}
                        paginator
                        rows={tableConfig.rows}
                        rowsPerPageOptions={tableConfig.rowsPerPageOptions}
                        paginatorTemplate={
                            screenSize.isMobile 
                                ? "PrevPageLink PageLinks NextPageLink" 
                                : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        }
                        currentPageReportTemplate="👥 {first}-{last} de {totalRecords}"
                        rowHover
                        tableStyle={{ 
                            minWidth: screenSize.isMobile ? '100%' : '800px',
                            width: '100%'
                        }}
                    >
                        <Column 
                            field="cveemp" 
                            header={screenSize.isMobile ? "🔑 Código" : "🔑 Código de Empleado"}
                            headerStyle={{ 
                                fontWeight: 'bold', 
                                textAlign: 'center',
                                backgroundColor: '#f8fafc',
                                padding: screenSize.isMobile ? '8px 4px' : '12px',
                                fontSize: screenSize.isMobile ? '0.75rem' : '0.9rem'
                            }}
                            style={{ 
                                width: screenSize.isMobile ? '80px' : '180px', 
                                textAlign: 'center',
                                padding: screenSize.isMobile ? '8px 4px' : '12px'
                            }}
                            body={codigoBodyTemplate}
                            sortable
                        />
                        <Column 
                            field="nomemp" 
                            header={screenSize.isMobile ? "👤 Nombre" : "👤 Nombre del Empleado"}
                            headerStyle={{ 
                                fontWeight: 'bold',
                                backgroundColor: '#f8fafc',
                                padding: screenSize.isMobile ? '8px 4px' : '12px',
                                fontSize: screenSize.isMobile ? '0.75rem' : '0.9rem'
                            }}
                            style={{ 
                                minWidth: screenSize.isMobile ? '120px' : '250px',
                                padding: screenSize.isMobile ? '8px 4px' : '12px'
                            }}
                            body={nombreBodyTemplate}
                            sortable
                        />
                        {/* Columna de Sucursal - oculta en móvil muy pequeño */}
                        {!screenSize.isMobile && (
                            <Column 
                                field="sucursal" 
                                header="🏢 Sucursal"
                                headerStyle={{ 
                                    fontWeight: 'bold', 
                                    textAlign: 'center',
                                    backgroundColor: '#f8fafc',
                                    padding: '12px',
                                    fontSize: '0.9rem'
                                }}
                                style={{ 
                                    width: screenSize.isTablet ? '120px' : '180px', 
                                    textAlign: 'center',
                                    padding: '12px'
                                }}
                                body={sucursalBodyTemplate}
                                sortable
                            />
                        )}
                        <Column 
                            header={screenSize.isMobile ? "🔐 Pass" : "🔐 Contraseña"}
                            headerStyle={{ 
                                fontWeight: 'bold', 
                                textAlign: 'center',
                                backgroundColor: '#f8fafc',
                                padding: screenSize.isMobile ? '8px 4px' : '12px',
                                fontSize: screenSize.isMobile ? '0.75rem' : '0.9rem'
                            }}
                            style={{ 
                                width: screenSize.isMobile ? '80px' : '200px', 
                                textAlign: 'center',
                                padding: screenSize.isMobile ? '8px 4px' : '12px'
                            }}
                            body={passwordBodyTemplate}
                        />
                        <Column 
                            header="⚙️"
                            headerStyle={{ 
                                fontWeight: 'bold', 
                                textAlign: 'center',
                                backgroundColor: '#f8fafc',
                                padding: screenSize.isMobile ? '8px 4px' : '12px',
                                fontSize: screenSize.isMobile ? '0.75rem' : '0.9rem'
                            }}
                            style={{ 
                                width: screenSize.isMobile ? '70px' : '120px', 
                                textAlign: 'center',
                                padding: screenSize.isMobile ? '8px 4px' : '12px'
                            }}
                            body={accionesBodyTemplate}
                        />
                    </DataTable>
                </div>

                {/* Dialog para crear/editar - RESPONSIVO */}
                <Dialog
                    header={dialogHeader}
                    visible={showDialog}
                    style={{ 
                        width: screenSize.isMobile ? '95vw' : screenSize.isTablet ? '80vw' : '500px',
                        maxWidth: '500px'
                    }}
                    modal
                    onHide={handleCancelar}
                    footer={
                        <div style={{ 
                            display: 'flex', 
                            gap: '10px', 
                            justifyContent: 'flex-end',
                            flexDirection: screenSize.isMobile ? 'column' : 'row'
                        }}>
                            <Button
                                label="Cancelar"
                                icon="pi pi-times"
                                severity="secondary"
                                outlined
                                onClick={handleCancelar}
                                disabled={submitting}
                                style={{ 
                                    borderRadius: '8px',
                                    width: screenSize.isMobile ? '100%' : 'auto'
                                }}
                            />
                            <Button
                                label={mode === 'create' ? 'Crear Empleado' : 'Actualizar'}
                                icon="pi pi-check"
                                severity="success"
                                onClick={handleGuardar}
                                loading={submitting}
                                disabled={submitting}
                                style={{ 
                                    borderRadius: '8px',
                                    width: screenSize.isMobile ? '100%' : 'auto'
                                }}
                            />
                        </div>
                    }
                >
                    <div style={{ padding: screenSize.isMobile ? '10px 0' : '20px 0' }}>
                        <form onSubmit={(e) => e.preventDefault()}>
                            
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '8px', 
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: screenSize.isMobile ? '0.9rem' : '1rem'
                                }}>
                                    🔑 Código de Empleado <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <InputText
                                    value={formState.cveemp}
                                    onChange={(e) => {
                                        updateFormState({ cveemp: e.target.value });
                                        if (errors.cveemp) {
                                            setErrors(prev => ({ ...prev, cveemp: undefined }));
                                        }
                                    }}
                                    placeholder="Ingrese el código del empleado"
                                    className={errors.cveemp ? 'p-invalid' : ''}
                                    style={{ 
                                        width: '100%',
                                        height: '40px',
                                        borderRadius: '8px',
                                        fontSize: screenSize.isMobile ? '0.9rem' : '1rem'
                                    }}
                                    maxLength={10}
                                    disabled={mode === 'edit'}
                                />
                                {errors.cveemp && (
                                    <small className="p-error" style={{ display: 'block', marginTop: '5px' }}>
                                        {errors.cveemp}
                                    </small>
                                )}
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '8px', 
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: screenSize.isMobile ? '0.9rem' : '1rem'
                                }}>
                                    👤 Nombre del Empleado <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <InputText
                                    value={formState.nomemp}
                                    onChange={(e) => {
                                        updateFormState({ nomemp: e.target.value });
                                        if (errors.nomemp) {
                                            setErrors(prev => ({ ...prev, nomemp: undefined }));
                                        }
                                    }}
                                    placeholder="Ingrese el nombre completo del empleado"
                                    className={errors.nomemp ? 'p-invalid' : ''}
                                    style={{ 
                                        width: '100%',
                                        height: '40px',
                                        borderRadius: '8px',
                                        fontSize: screenSize.isMobile ? '0.9rem' : '1rem'
                                    }}
                                    maxLength={200}
                                />
                                {errors.nomemp && (
                                    <small className="p-error" style={{ display: 'block', marginTop: '5px' }}>
                                        {errors.nomemp}
                                    </small>
                                )}
                            </div>

                            {/* Campo de Sucursal - CORREGIDO */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '8px', 
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: screenSize.isMobile ? '0.9rem' : '1rem'
                                }}>
                                    🏢 Sucursal <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>(opcional)</span>
                                </label>
                                <InputText
                                    value={formState.sucursal}
                                    onChange={(e) => {
                                        updateFormState({ sucursal: e.target.value });
                                        if (errors.sucursal) {
                                            setErrors(prev => ({ ...prev, sucursal: undefined }));
                                        }
                                    }}
                                    placeholder="Ingrese el código de sucursal (ej: 001, 002)"
                                    className={errors.sucursal ? 'p-invalid' : ''}
                                    style={{ 
                                        width: '100%',
                                        height: '40px',
                                        borderRadius: '8px',
                                        fontSize: screenSize.isMobile ? '0.9rem' : '1rem'
                                    }}
                                    maxLength={15}
                                />  {errors.sucursal && (
                                    <small className="p-error" style={{ display: 'block', marginTop: '5px' }}>
                                        {errors.sucursal}
                                    </small>
                                )}
                                <small style={{
                                    display: 'block',
                                    marginTop: '5px',
                                    color: '#6b7280',
                                    fontSize: screenSize.isMobile ? '0.75rem' : '0.8rem'
                                }}>
                                    Código de sucursal (máximo 15 caracteres)
                                </small>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: screenSize.isMobile ? '0.9rem' : '1rem'
                                }}>
                                    🔐 Contraseña <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <Password
                                    value={formState.password}
                                    onChange={(e) => {
                                        updateFormState({ password: e.target.value });
                                        if (errors.password) {
                                            setErrors(prev => ({ ...prev, password: undefined }));
                                        }
                                    }}
                                    placeholder="Ingrese la contraseña"
                                    feedback={mode === 'create'}
                                    toggleMask
                                    className={errors.password ? 'p-invalid' : ''}
                                    style={{ width: '100%' }}
                                    inputStyle={{
                                        width: '100%',
                                        height: '40px',
                                        borderRadius: '8px',
                                        fontSize: screenSize.isMobile ? '0.9rem' : '1rem'
                                    }}
                                    promptLabel="Ingrese una contraseña"
                                    weakLabel="Débil"
                                    mediumLabel="Media"
                                    strongLabel="Fuerte"
                                />
                                {errors.password && (
                                    <small className="p-error" style={{ display: 'block', marginTop: '5px' }}>
                                        {errors.password}
                                    </small>
                                )}
                                <small style={{
                                    display: 'block',
                                    marginTop: '5px',
                                    color: '#6b7280',
                                    fontSize: screenSize.isMobile ? '0.75rem' : '0.8rem'
                                }}>
                                    La contraseña debe tener entre 6 y 100 caracteres
                                </small>
                            </div>

                        </form>
                    </div>
                </Dialog>

                {/* Dialog para carga masiva - RESPONSIVO */}
                <Dialog
                    header="📊 Carga Masiva de Empleados"
                    visible={showCargaMasivaDialog}
                    style={{
                        width: screenSize.isMobile ? '95vw' : screenSize.isTablet ? '90vw' : '700px',
                        maxWidth: '700px'
                    }}
                    modal
                    onHide={handleCancelarCargaMasiva}
                    footer={
                        <div style={{
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'space-between',
                            flexDirection: screenSize.isMobile ? 'column' : 'row'
                        }}>
                            <Button
                                label="Descargar Plantilla"
                                icon="pi pi-download"
                                severity="info"
                                outlined
                                onClick={descargarPlantillaExcel}
                                style={{
                                    borderRadius: '8px',
                                    width: screenSize.isMobile ? '100%' : 'auto'
                                }}
                            />
                            <Button
                                label="Cancelar"
                                icon="pi pi-times"
                                severity="secondary"
                                outlined
                                onClick={handleCancelarCargaMasiva}
                                disabled={procesandoCargaMasiva}
                                style={{
                                    borderRadius: '8px',
                                    width: screenSize.isMobile ? '100%' : 'auto'
                                }}
                            />
                        </div>
                    }
                >
                    <div style={{ padding: screenSize.isMobile ? '10px 0' : '20px 0' }}>
                        <Card style={{
                            marginBottom: '20px',
                            backgroundColor: '#e3f2fd',
                            border: '1px solid #90caf9',
                            borderRadius: '8px'
                        }}>
                            <div style={{ padding: '15px' }}>
                                <h6 style={{
                                    margin: '0 0 10px 0',
                                    color: '#1565c0',
                                    fontWeight: '600',
                                    fontSize: screenSize.isMobile ? '0.9rem' : '1rem'
                                }}>
                                    📋 Instrucciones
                                </h6>
                                <ul style={{
                                    margin: '0',
                                    paddingLeft: '20px',
                                    color: '#1565c0',
                                    fontSize: screenSize.isMobile ? '0.8rem' : '0.9rem'
                                }}>
                                    <li>El archivo debe ser formato Excel (.xlsx)</li>
                                    <li>Debe contener las columnas: <strong>cveemp</strong> y <strong>nomemp</strong></li>
                                    <li>Columna opcional: <strong>sucursal</strong></li>
                                    <li>Los códigos de empleado deben ser únicos</li>
                                    <li><strong>Las contraseñas se generan automáticamente</strong></li>
                                    <li>Los nombres no pueden exceder 200 caracteres</li>
                                    <li>Las sucursales no pueden exceder 15 caracteres</li>
                                </ul>
                            </div>
                        </Card>

                        <Card style={{
                            marginBottom: '20px',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '8px'
                        }}>
                            <div style={{ padding: '15px' }}>
                                <h6 style={{
                                    margin: '0 0 10px 0',
                                    color: '#856404',
                                    fontWeight: '600',
                                    fontSize: screenSize.isMobile ? '0.9rem' : '1rem'
                                }}>
                                    🔐 Generación Automática de Contraseñas
                                </h6>
                                <p style={{
                                    margin: '0 0 10px 0',
                                    color: '#856404',
                                    fontSize: screenSize.isMobile ? '0.8rem' : '0.9rem'
                                }}>
                                    Las contraseñas se crean usando las <strong>iniciales del nombre</strong> + <strong>últimos dígitos del código</strong>:
                                </p>
                                <div style={{
                                    backgroundColor: '#fff',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    fontFamily: 'monospace',
                                    fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem',
                                    border: '1px solid #ffc107',
                                    overflowX: 'auto'
                                }}>
                                    <div>• 7895 + "Ivan Armando Inés Galicia" → <strong>IAIG95</strong></div>
                                    <div>• 97 + "José Gerardo Soto" → <strong>JGS97</strong></div>
                                    <div>• 9 + "Juan Pérez López" → <strong>JPL9</strong></div>
                                </div>
                            </div>
                        </Card>

                        {procesandoCargaMasiva ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <h6 style={{
                                    color: '#374151',
                                    marginBottom: '15px',
                                    fontSize: screenSize.isMobile ? '0.9rem' : '1rem'
                                }}>
                                    Procesando empleados...
                                </h6>
                                <ProgressBar
                                    value={progresoCarga}
                                    style={{
                                        marginTop: '10px',
                                        height: screenSize.isMobile ? '6px' : '8px'
                                    }}
                                    color="#3b82f6"
                                />
                                <p style={{
                                    marginTop: '10px',
                                    color: '#6b7280',
                                    fontWeight: '600',
                                    fontSize: screenSize.isMobile ? '0.8rem' : '0.9rem'
                                }}>
                                    {Math.round(progresoCarga)}% completado
                                </p>
                            </div>
                        ) : (
                            <FileUpload
                                ref={fileUploadRef}
                                mode="basic"
                                name="excel"
                                accept=".xlsx,.xls"
                                maxFileSize={5000000}
                                onSelect={handleArchivoSeleccionado}
                                chooseLabel={screenSize.isMobile ? "📁 Seleccionar Excel" : "📁 Seleccionar archivo Excel"}
                                className="p-button-outlined"
                                style={{
                                    width: '100%',
                                    borderRadius: '8px'
                                }}
                            />
                        )}
                    </div>
                </Dialog>

                {/* Dialog para mostrar resultados de carga masiva - COMPLETAMENTE RESPONSIVO */}
                <Dialog
                    header="📊 Resultados de Carga Masiva"
                    visible={showResultadoDialog}
                    style={{
                        width: screenSize.isMobile ? '95vw' : screenSize.isTablet ? '90vw' : '1000px',
                        maxWidth: '1000px',
                        maxHeight: '90vh'
                    }}
                    modal
                    onHide={() => setShowResultadoDialog(false)}
                    footer={
                        <Button
                            label="Cerrar"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            onClick={() => setShowResultadoDialog(false)}
                            style={{
                                borderRadius: '8px',
                                width: screenSize.isMobile ? '100%' : 'auto'
                            }}
                        />
                    }
                >
                    {resultadoCarga && (
                        <div style={{ padding: screenSize.isMobile ? '10px 0' : '20px 0' }}>
                            {/* Resumen - RESPONSIVO */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: screenSize.isMobile ? '1fr' : 'repeat(3, 1fr)',
                                gap: '15px',
                                marginBottom: '20px'
                            }}>
                                <Card style={{
                                    backgroundColor: '#d4edda',
                                    textAlign: 'center',
                                    border: '1px solid #c3e6cb',
                                    borderRadius: '8px'
                                }}>
                                    <div style={{ padding: screenSize.isMobile ? '12px' : '15px' }}>
                                        <h3 style={{
                                            margin: '0',
                                            color: '#155724',
                                            fontSize: screenSize.isMobile ? '1.5rem' : '2rem'
                                        }}>
                                            {resultadoCarga.exitosos}
                                        </h3>
                                        <p style={{
                                            margin: '5px 0 0 0',
                                            color: '#155724',
                                            fontWeight: '600',
                                            fontSize: screenSize.isMobile ? '0.8rem' : '0.9rem'
                                        }}>
                                            ✅ Exitosos
                                        </p>
                                    </div>
                                </Card>
                                <Card style={{
                                    backgroundColor: '#f8d7da',
                                    textAlign: 'center',
                                    border: '1px solid #f5c6cb',
                                    borderRadius: '8px'
                                }}>
                                    <div style={{ padding: screenSize.isMobile ? '12px' : '15px' }}>
                                        <h3 style={{
                                            margin: '0',
                                            color: '#721c24',
                                            fontSize: screenSize.isMobile ? '1.5rem' : '2rem'
                                        }}>
                                            {resultadoCarga.errores}
                                        </h3>
                                        <p style={{
                                            margin: '5px 0 0 0',
                                            color: '#721c24',
                                            fontWeight: '600',
                                            fontSize: screenSize.isMobile ? '0.8rem' : '0.9rem'
                                        }}>
                                            ❌ Errores
                                        </p>
                                    </div>
                                </Card>
                                <Card style={{
                                    backgroundColor: '#fff3cd',
                                    textAlign: 'center',
                                    border: '1px solid #ffeaa7',
                                    borderRadius: '8px'
                                }}>
                                    <div style={{ padding: screenSize.isMobile ? '12px' : '15px' }}>
                                        <h3 style={{
                                            margin: '0',
                                            color: '#856404',
                                            fontSize: screenSize.isMobile ? '1.5rem' : '2rem'
                                        }}>
                                            {resultadoCarga.duplicados}
                                        </h3>
                                        <p style={{
                                            margin: '5px 0 0 0',
                                            color: '#856404',
                                            fontWeight: '600',
                                            fontSize: screenSize.isMobile ? '0.8rem' : '0.9rem'
                                        }}>
                                            ⚠️ Duplicados
                                        </p>
                                    </div>
                                </Card>
                            </div>

                            <Divider />

                            {/* Detalles - TABLA RESPONSIVA */}
                            <div style={{
                                maxHeight: screenSize.isMobile ? '300px' : '400px',
                                overflow: 'auto'
                            }}>
                                <DataTable
                                    value={resultadoCarga.detalles}
                                    size="small"
                                    stripedRows
                                    emptyMessage="No hay detalles"
                                    style={{
                                        borderRadius: '8px',
                                        fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem'
                                    }}
                                    responsiveLayout={screenSize.isMobile ? 'stack' : 'scroll'}
                                    breakpoint="768px"
                                    scrollable={!screenSize.isMobile}
                                    scrollHeight={screenSize.isMobile ? "250px" : "350px"}
                                >
                                    <Column
                                        field="fila"
                                        header="Fila"
                                        style={{
                                            width: screenSize.isMobile ? '50px' : '60px',
                                            textAlign: 'center',
                                            fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem'
                                        }}
                                    />
                                    <Column
                                        field="cveemp"
                                        header="Código"
                                        style={{
                                            width: screenSize.isMobile ? '80px' : '100px',
                                            textAlign: 'center',
                                            fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem'
                                        }}
                                    />
                                    <Column
                                        field="nomemp"
                                        header="Nombre"
                                        style={{
                                            minWidth: screenSize.isMobile ? '120px' : '200px',
                                            fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem'
                                        }}
                                    />
                                    {/* Columna de Sucursal - oculta en móvil */}
                                    {!screenSize.isMobile && (
                                        <Column
                                            field="sucursal"
                                            header="Sucursal"
                                            style={{
                                                width: '120px',
                                                textAlign: 'center',
                                                fontSize: '0.8rem'
                                            }}
                                            body={(rowData) => (
                                                rowData.sucursal && rowData.sucursal.trim() !== '' ? (
                                                    <Badge
                                                        value={rowData.sucursal}
                                                        severity="success"
                                                        style={{
                                                            fontFamily: 'monospace',
                                                            fontSize: '0.7rem'
                                                        }}
                                                    />
                                                ) : (
                                                    <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>-</span>
                                                )
                                            )}
                                        />
                                    )}
                                    <Column
                                        field="passwordGenerada"
                                        header={screenSize.isMobile ? "Pass" : "Contraseña"}
                                        style={{
                                            width: screenSize.isMobile ? '80px' : '120px',
                                            textAlign: 'center',
                                            fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem'
                                        }}
                                        body={(rowData) => (
                                            <Badge
                                                value={rowData.passwordGenerada || 'N/A'}
                                                severity="secondary"
                                                style={{
                                                    fontFamily: 'monospace',
                                                    fontSize: screenSize.isMobile ? '0.6rem' : '0.7rem'
                                                }}
                                            />
                                        )}
                                    />
                                    <Column
                                        field="estado"
                                        header="Estado"
                                        style={{
                                            width: screenSize.isMobile ? '80px' : '100px',
                                            textAlign: 'center',
                                            fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem'
                                        }}
                                        body={(rowData) => (
                                            <Badge
                                                value={rowData.estado.toUpperCase()}
                                                severity={
                                                    rowData.estado === 'exitoso' ? 'success' :
                                                    rowData.estado === 'error' ? 'danger' : 'warning'
                                                }
                                                style={{
                                                    fontSize: screenSize.isMobile ? '0.6rem' : '0.7rem',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        )}
                                    />
                                    <Column
                                        field="error"
                                        header="Error"
                                        style={{
                                            minWidth: screenSize.isMobile ? '100px' : '150px',
                                            fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem'
                                        }}
                                        body={(rowData) => (
                                            rowData.error ? (
                                                <span style={{
                                                    color: '#dc2626',
                                                    fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem',
                                                    fontWeight: '500'
                                                }}>
                                                    {rowData.error}
                                                </span>
                                            ) : (
                                                <span style={{
                                                    color: '#10b981',
                                                    fontSize: screenSize.isMobile ? '0.7rem' : '0.8rem'
                                                }}>
                                                    ✓ Sin errores
                                                </span>
                                            )
                                        )}
                                    />
                                </DataTable>
                            </div>
                        </div>
                    )}
                </Dialog>

                {/* Estilos CSS adicionales para responsive */}
                <style>
                    {`
                        /* Estilos responsivos adicionales */
                        @media (max-width: 768px) {
                            .p-datatable .p-datatable-thead > tr > th {
                                padding: 8px 4px !important;
                                font-size: 0.75rem !important;
                            }
                            
                            .p-datatable .p-datatable-tbody > tr > td {
                                padding: 8px 4px !important;
                                font-size: 0.75rem !important;
                            }
                            
                            .p-paginator {
                                padding: 8px !important;
                            }
                            
                            .p-paginator .p-paginator-pages .p-paginator-page {
                                min-width: 2rem !important;
                                height: 2rem !important;
                                font-size: 0.8rem !important;
                            }
                            
                            .p-dialog .p-dialog-header {
                                padding: 1rem !important;
                            }
                            
                            .p-dialog .p-dialog-content {
                                padding: 0 1rem 1rem 1rem !important;
                            }
                            
                            .p-dialog .p-dialog-footer {
                                padding: 1rem !important;
                            }
                            
                            .p-fileupload .p-button {
                                font-size: 0.8rem !important;
                                padding: 0.5rem 1rem !important;
                            }
                            
                            .p-card .p-card-body {
                                padding: 1rem !important;
                            }
                            
                            .p-badge {
                                font-size: 0.7rem !important;
                                padding: 2px 6px !important;
                            }
                        }
                        
                        @media (max-width: 480px) {
                            .p-datatable .p-datatable-thead > tr > th {
                                padding: 6px 2px !important;
                                font-size: 0.7rem !important;
                            }
                            
                            .p-datatable .p-datatable-tbody > tr > td {
                                padding: 6px 2px !important;
                                font-size: 0.7rem !important;
                            }
                            
                            .p-badge {
                                font-size: 0.6rem !important;
                                padding: 1px 4px !important;
                            }
                            
                            .p-button {
                                font-size: 0.75rem !important;
                                padding: 0.4rem 0.8rem !important;
                            }
                            
                            .p-inputtext {
                                font-size: 0.8rem !important;
                            }
                        }
                        
                        /* Mejoras para la tabla en modo stack (móvil) */
                        .p-datatable.p-datatable-responsive-stack .p-datatable-tbody > tr > td {
                            border: 0 none !important;
                            width: 100% !important;
                            display: flex !important;
                            align-items: center !important;
                            justify-content: space-between !important;
                            padding: 0.5rem !important;
                        }
                        
                        .p-datatable.p-datatable-responsive-stack .p-datatable-tbody > tr > td:before {
                            font-weight: bold !important;
                            min-width: 30% !important;
                        }
                        
                        /* Estilos para mejorar la legibilidad en móvil */
                        .p-datatable.p-datatable-responsive-stack .p-datatable-tbody > tr {
                            background: white !important;
                            border: 1px solid #e5e7eb !important;
                            border-radius: 8px !important;
                            margin-bottom: 1rem !important;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
                        }
                        
                        /* Ocultar header en modo stack */
                        .p-datatable.p-datatable-responsive-stack .p-datatable-thead {
                            display: none !important;
                        }
                        
                        /* Estilos para el progress bar en móvil */
                        @media (max-width: 768px) {
                            .p-progressbar {
                                height: 6px !important;
                            }
                            
                            .p-progressbar .p-progressbar-value {
                                background: linear-gradient(90deg, #3b82f6, #10b981) !important;
                            }
                        }
                        
                        /* Estilos para los diálogos en móvil */
                        @media (max-width: 768px) {
                            .p-dialog {
                                margin: 1rem !important;
                            }
                            
                            .p-dialog .p-dialog-header .p-dialog-title {
                                font-size: 1rem !important;
                            }
                            
                            .p-overlaypanel {
                                max-width: 90vw !important;
                            }
                        }
                        
                        /* Animaciones suaves para transiciones responsive */
                        .p-datatable, .p-dialog, .p-card {
                            transition: all 0.3s ease !important;
                        }
                        
                        /* Mejoras para accesibilidad en móvil */
                        @media (max-width: 768px) {
                            .p-button {
                                min-height: 44px !important;
                                min-width: 44px !important;
                            }
                            
                            .p-inputtext, .p-password input {
                                min-height: 44px !important;
                            }
                        }
                        
                        /* Estilos específicos para el componente de empleados */
                        .empleados-admin-container {
                            transition: all 0.3s ease;
                        }
                        
                        /* Mejoras para la visualización de badges */
                        .p-badge.p-component {
                            display: inline-flex;
                            align-items: center;
                            justify-content: center;
                            line-height: 1;
                        }
                        
                        /* Estilos para mejorar la legibilidad de texto largo */
                        .empleado-nombre {
                            word-wrap: break-word;
                            overflow-wrap: break-word;
                            hyphens: auto;
                        }
                        
                        /* Estilos para el FileUpload responsivo */
                        .p-fileupload .p-fileupload-buttonbar {
                            flex-wrap: wrap;
                            gap: 0.5rem;
                        }
                        
                        @media (max-width: 768px) {
                            .p-fileupload .p-fileupload-buttonbar .p-button {
                                flex: 1;
                                min-width: 0;
                            }
                        }
                        
                        /* Mejoras para el scroll horizontal en tablas pequeñas */
                        @media (max-width: 768px) {
                            .p-datatable-wrapper {
                                overflow-x: auto;
                            }
                            
                            .p-datatable table {
                                min-width: 600px;
                            }
                        }
                        
                        /* Estilos para mejorar la experiencia táctil en móvil */
                        @media (max-width: 768px) {
                            .p-button:not(.p-button-icon-only) {
                                padding: 0.75rem 1rem !important;
                            }
                            
                            .p-inputtext {
                                padding: 0.75rem !important;
                            }
                        }
                        
                        /* Animaciones para feedback visual */
                        .p-button:active {
                            transform: scale(0.98);
                            transition: transform 0.1s ease;
                        }
                        
                        /* Estilos para mejorar el contraste en modo claro */
                        .p-datatable .p-datatable-thead > tr > th {
                            background-color: #f8fafc !important;
                            color: #374151 !important;
                            border-bottom: 2px solid #e5e7eb !important;
                        }
                        
                        /* Hover effects para mejor UX */
                        .p-datatable .p-datatable-tbody > tr:hover {
                            background-color: #f9fafb !important;
                            transition: background-color 0.2s ease;
                        }
                        
                        /* Estilos para loading states */
                        .p-datatable .p-datatable-loading-overlay {
                            background-color: rgba(255, 255, 255, 0.8) !important;
                        }
                        
                        /* Mejoras para el componente Password */
                        .p-password .p-password-panel {
                            padding: 1rem;
                            border-radius: 8px;
                        }
                        
                        @media (max-width: 768px) {
                            .p-password .p-password-panel {
                                padding: 0.75rem;
                                font-size: 0.8rem;
                            }
                        }
                    `}
                </style>
            </div>
        </>
    );
};

export default EmpleadosAdmin;